import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not logged in

  const checkSession = useCallback(async () => {
    // If we have a valid token, try to use it.
    // If we only have stored credentials but no token, the response interceptor
    // will silently re-login on the first 401.
    if (!auth.hasStoredSession()) {
      setUser(null);
      return;
    }
    try {
      const u = await auth.me();
      setUser(u);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkSession();
    // Re-validate session when the app comes back to the foreground.
    // On iOS PWA this is critical because the OS can wipe cookies in the background.
    const onFocus = () => {
      if (!auth.hasStoredSession()) return;
      auth.me().catch(() => {
        // Interceptor already tried silent re-login. If it still fails, log out.
        if (!auth.hasStoredSession()) setUser(null);
      });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onFocus();
    });
    return () => window.removeEventListener("focus", onFocus);
  }, [checkSession]);

  const login = async (email, password) => {
    const u = await auth.login(email, password);
    setUser(u);
    return u;
  };

  const logout = async () => {
    await auth.logout();
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
